import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, Heart, Calendar, Shield, Brain, Eye, Target, Users, Award, Clock, Lightbulb, Sparkles, MessageCircle } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-300 rounded-lg mb-4 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/10" : "bg-white";
  const borderColor = type === "important" ? "border-[#ab3d22]/30" : "border-gray-300";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6 shadow-sm`}>
      {children}
    </div>
  );
};

const AboutPage = () => {
  const [openSections, setOpenSections] = useState({
    whoWeAre: true,
    whatWeDo: false,
    howWeWork: false,
    whereWereGoing: false,
    whatWeStandFor: false,
    contact: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

    return (
      <div className="min-h-screen bg-[#f8f8f8]">
        <Navbar />
      
      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#d6dbd8]"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-rose-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-amber-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:min-h-[200px] w-full">
            <div className="text-left flex-1 max-w-lg">
              {/* Main Headline */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                About Us
                {/* Brand Accent Line */}
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-[#ab3d22] rounded-full"></div>
              </h1>
              
              {/* Refined Tagline */}
              <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light">
                Cutting through the noise so you can find what actually works.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: December 8, 2025</span>
          </div>

          {/* Who We Are */}
          <div id="who-we-are">
            <AccordionItem
              title="Who We Are"
              isOpen={openSections.whoWeAre}
              onToggle={() => toggleSection('whoWeAre')}
            >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance was created for one simple reason: beauty shopping has become overwhelming.
              </p>
              
              <p className="text-sm text-gray-600">
                With endless launches, endless opinions, and endless hype, it's harder than ever to know what genuinely works — and what's just loud marketing.
              </p>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Our Purpose</p>
                    <p className="text-sm">
                      We built Radiance to bring clarity back to beauty and wellness.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
            </AccordionItem>
          </div>

          {/* What We Do */}
          <AccordionItem
            title="What We Do"
            isOpen={openSections.whatWeDo}
            onToggle={() => toggleSection('whatWeDo')}
          >
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Target className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">A Beauty Discovery Platform</h4>
                  <p className="text-sm text-gray-600">
                    Radiance is a beauty discovery platform designed to help you make confident decisions without the chaos.
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                We use AI to sift through the overwhelming amount of information in the beauty world — conversations, trends, patterns, and product chatter — and transform it into clear, helpful recommendations. Then we apply human editorial judgment to make it practical, honest, and easy to understand.
              </p>
              
              <CalloutBox>
                <p className="text-sm">
                  <strong>It's not about testing every product.</strong> It's about interpreting the collective experience of the beauty community and making it useful.
                </p>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* How We Work */}
          <AccordionItem
            title="How We Work"
            isOpen={openSections.howWeWork}
            onToggle={() => toggleSection('howWeWork')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Radiance combines:
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Brain className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Pattern-finding AI</h4>
                    <p className="text-sm text-gray-600">
                      Helps surface what people consistently love, notice, and repurchase across the beauty world.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Human editorial clarity</h4>
                    <p className="text-sm text-gray-600">
                      Every recommendation is reviewed and shaped by people who understand routines, textures, and what shoppers actually care about.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Eye className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Real-world insights</h4>
                    <p className="text-sm text-gray-600">
                      Our content focuses on the things beauty users talk about most: how something feels, how it performs, who it suits, and where it shines in a routine.
                    </p>
                  </div>
                </div>
              </div>
              
              <CalloutBox type="important">
                <p className="text-sm">
                  This gives you guidance that feels grounded, practical, and genuinely helpful — without the overwhelm.
                </p>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Where Radiance Is Going */}
          <AccordionItem
            title="Where Radiance Is Going"
            isOpen={openSections.whereWereGoing}
            onToggle={() => toggleSection('whereWereGoing')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We're building Radiance in phases.
              </p>
              
              <p className="text-sm text-gray-600">
                Right now, we use AI to help identify what the beauty world is talking about.
                But the future of Radiance is <strong>your real experiences</strong>.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Our long-term vision:</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <MessageCircle className="h-4 w-4 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                    <span>A community where real users share their routines, results, and reviews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="h-4 w-4 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                    <span>A rating system powered by people, not platforms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                    <span>Discovery that gets smarter every time someone contributes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                    <span>A trusted space where noise becomes clarity</span>
                  </li>
                </ul>
              </div>
              
              <CalloutBox type="important">
                <p className="text-sm">
                  <strong>AI helps us start.</strong> Your voices will shape what Radiance becomes.
                </p>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* What We Stand For */}
          <AccordionItem
            title="What We Stand For"
            isOpen={openSections.whatWeStandFor}
            onToggle={() => toggleSection('whatWeStandFor')}
          >
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-[#ab3d22]" />
                    <h4 className="font-semibold text-gray-900">Clarity Over Confusion</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    We simplify decisions so beauty feels empowering, not overwhelming.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-5 w-5 text-[#ab3d22]" />
                    <h4 className="font-semibold text-gray-900">Honesty Over Hype</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    If a product is featured on Radiance, it's because it earned its spot — not because it paid for it.
                  </p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-5 w-5 text-[#ab3d22]" />
                    <h4 className="font-semibold text-gray-900">User First, Always</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Our mission is to help you make better choices with less stress.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="h-5 w-5 text-[#ab3d22]" />
                    <h4 className="font-semibold text-gray-900">Forward Thinking</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    We're building the beauty platform we wish existed: more transparent, more helpful, and more human.
                  </p>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* Contact */}
          <AccordionItem
            title="Get in Touch"
            isOpen={openSections.contact}
            onToggle={() => toggleSection('contact')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Have questions, feedback, or product suggestions?
                We'd love to hear from you as Radiance continues to grow.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Contact Us:</span>
                    </div>
                    <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all sm:ml-0 ml-6">
                      hello@meetradiance.com
                    </a>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Response Time:</span>
                    </div>
                    <span className="text-sm text-gray-600 sm:ml-0 ml-6">Within 24 hours</span>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>
        </div>
      </Container>
      
      <Footer />
    </div>
  );
};

export default AboutPage;
